import { Link, useNavigate } from "react-router-dom";
import type { LeadDetail } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import { useLinkedLeadDecision } from "@/hooks/useLinkedLeadDecision";
import { linkedLeadsOf } from "@/lib/linked-leads";

type Props = { lead: LeadDetail };


/**
 * An unverified link between this lead and another one (docs/LEAD_MERGE.md): an
 * Instagram prospect who typed the phone an existing lead already has.
 *
 * Shown on both sides. On the target it lists the Instagram conversations already
 * folded into its timeline; on the Instagram lead it names the lead it was linked
 * to. Either way the team decides: the agent never reads across the link until it
 * is confirmed, because a typed phone proves nothing.
 */
export function LinkedLeadsBanner({ lead }: Props): React.JSX.Element | null {
  const links = linkedLeadsOf(lead);
  if (links.length === 0 && !lead.linkedTo) return null;
  return (
    <div className="flex flex-col gap-2">
      {links.map((linked) => (
        <LinkRow
          key={linked.id}
          targetId={lead.id}
          sourceId={linked.id}
          text={
            linked.instagramUsername
              ? `También escribe por Instagram como @${linked.instagramUsername}`
              : `También escribe por Instagram (${linked.sessionRef})`
          }
          leaveOnConfirm={false}
        />
      ))}
      {lead.linkedTo && (
        <LinkRow
          targetId={lead.linkedTo.id}
          sourceId={lead.id}
          text={
            <>
              Dio el teléfono de{" "}
              <Link to={`/leads/${lead.linkedTo.id}`} className="font-semibold underline">
                {lead.linkedTo.name ?? lead.linkedTo.sessionRef}
              </Link>
            </>
          }
          leaveOnConfirm
        />
      )}
    </div>
  );
}

function LinkRow({
  targetId,
  sourceId,
  text,
  leaveOnConfirm,
}: {
  targetId: string;
  sourceId: string;
  text: React.ReactNode;
  /** True on the Instagram lead itself: it stops existing once confirmed. */
  leaveOnConfirm: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const { mutate, isPending } = useLinkedLeadDecision(targetId);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <p className="flex-1 min-w-[12rem]">
        {text} <span className="text-amber-600">· sin verificar</span>
      </p>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={isPending}
          onClick={() =>
            mutate(
              { sourceId, decision: "confirm" },
              { onSuccess: () => (leaveOnConfirm ? navigate(`/leads/${targetId}`) : undefined) },
            )
          }
        >
          Confirmar
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isPending}
          onClick={() => mutate({ sourceId, decision: "reject" })}
        >
          No es la misma persona
        </Button>
      </div>
    </div>
  );
}
